import {
  Alert24Regular,
  DocumentText24Regular,
  PersonAdd24Regular,
  Checkmark24Regular,
  MoneyHand24Regular,
  BuildingMultiple24Regular,
  MoreVertical20Regular,
  CheckmarkCircle20Regular,
  Delete20Regular,
  Filter20Regular,
  Mail24Regular,
  MailRead24Regular,
} from "@fluentui/react-icons";
import { useState } from "react";
import { PageHeader } from "../../components/PageHeader";

interface Notification {
  id: string;
  icon: "alert" | "document" | "person" | "checkmark" | "money" | "building";
  title: string;
  description: string;
  timestamp: string;
  isRead: boolean;
  categoria: string;
}

export function NotificacoesView() {
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<
    "todas" | "nao-lidas" | "lidas"
  >("todas");

  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: "1",
      icon: "checkmark",
      title: "Novo orçamento aprovado",
      description: "O cliente ABC Construções aprovou o orçamento OBR-2024-032.",
      timestamp: "há 5 min",
      isRead: false,
      categoria: "Orçamentos",
    },
    {
      id: "2",
      icon: "person",
      title: "Novo cliente adicionado",
      description: "Manuel Silva foi adicionado à base de dados de clientes.",
      timestamp: "há 1 hora",
      isRead: false,
      categoria: "Clientes",
    },
    {
      id: "3",
      icon: "alert",
      title: "Prazo de entrega aproxima-se",
      description: "A obra Edifício Central tem prazo de entrega em 5 dias.",
      timestamp: "há 2 horas",
      isRead: true,
      categoria: "Obras",
    },
    {
      id: "4",
      icon: "document",
      title: "Novo documento enviado",
      description: "Fornecedor XYZ enviou fatura #1234 para aprovação.",
      timestamp: "há 3 horas",
      isRead: false,
      categoria: "Documentos",
    },
    {
      id: "5",
      icon: "money",
      title: "Pagamento aprovado",
      description: "O pagamento de 15 000,00 € foi processado com sucesso.",
      timestamp: "há 5 horas",
      isRead: true,
      categoria: "Financeiro",
    },
    {
      id: "6",
      icon: "building",
      title: "Nova obra criada",
      description:
        "A obra Remodelação Escritórios foi criada e atribuída à equipa.",
      timestamp: "há 1 dia",
      isRead: true,
      categoria: "Obras",
    },
    {
      id: "7",
      icon: "alert",
      title: "Ação necessária",
      description: "Subempreiteiro ABC aguarda aprovação de timesheet.",
      timestamp: "há 1 dia",
      isRead: false,
      categoria: "Timesheets",
    },
    {
      id: "8",
      icon: "document",
      title: "Contrato assinado",
      description: "Cliente XYZ Lda assinou o contrato da obra #OBR-2024-028.",
      timestamp: "há 2 dias",
      isRead: true,
      categoria: "Documentos",
    },
    {
      id: "9",
      icon: "checkmark",
      title: "Orçamento aceite",
      description: "O orçamento OBR-2024-030 foi aceite pelo cliente.",
      timestamp: "há 3 dias",
      isRead: true,
      categoria: "Orçamentos",
    },
    {
      id: "10",
      icon: "person",
      title: "Novo utilizador registado",
      description: "Ana Costa foi adicionada como Gestora de Obras.",
      timestamp: "há 5 dias",
      isRead: true,
      categoria: "Utilizadores",
    },
  ]);

  const getNotificationIcon = (icon: Notification["icon"]) => {
    const iconClass = "w-6 h-6";
    switch (icon) {
      case "alert":
        return <Alert24Regular className={iconClass} />;
      case "document":
        return <DocumentText24Regular className={iconClass} />;
      case "person":
        return <PersonAdd24Regular className={iconClass} />;
      case "checkmark":
        return <Checkmark24Regular className={iconClass} />;
      case "money":
        return <MoneyHand24Regular className={iconClass} />;
      case "building":
        return <BuildingMultiple24Regular className={iconClass} />;
    }
  };

  const getIconColor = (icon: Notification["icon"]) => {
    switch (icon) {
      case "alert":
        return "bg-red-500/10 text-red-600";
      case "document":
        return "bg-blue-500/10 text-blue-700";
      case "person":
        return "bg-gray-500/10 text-gray-700";
      case "checkmark":
        return "bg-green-500/10 text-green-600";
      case "money":
        return "bg-yellow-500/10 text-yellow-600";
      case "building":
        return "bg-primary/10 text-primary";
    }
  };

  const handleMarkAsRead = (id: string) => {
    setNotifications(
      notifications.map((n) =>
        n.id === id ? { ...n, isRead: true } : n,
      ),
    );
    setActiveMenuId(null);
  };

  const handleMarkAsUnread = (id: string) => {
    setNotifications(
      notifications.map((n) =>
        n.id === id ? { ...n, isRead: false } : n,
      ),
    );
    setActiveMenuId(null);
  };

  const handleDelete = (id: string) => {
    setNotifications(notifications.filter((n) => n.id !== id));
    setActiveMenuId(null);
  };

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, isRead: true })));
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filterStatus === "nao-lidas") return !n.isRead;
    if (filterStatus === "lidas") return n.isRead;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const actions = (
    <div className="flex items-center gap-3">
      {unreadCount > 0 && (
        <button
          onClick={handleMarkAllAsRead}
          className="h-10 px-4 bg-[var(--secondary)] border border-[var(--border)] text-[var(--secondary-foreground)] hover:bg-[var(--accent)] transition-all flex items-center gap-2"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          <CheckmarkCircle20Regular />
          <span className="hidden sm:inline">Marcar todas como lidas</span>
        </button>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-background">
      <PageHeader title="Notificações" actions={actions} />

      <div className="px-6 pt-6 pb-4 border-b border-[var(--border)]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter20Regular className="text-[var(--muted-foreground)]" />
            <span className="text-sm text-[var(--muted-foreground)]">
              Filtrar:
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus("todas")}
            className={`px-3 py-1.5 text-sm transition-all ${
              filterStatus === "todas"
                ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
                : "bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--accent)]"
            }`}
            style={{ borderRadius: "var(--radius)" }}
          >
            Todas ({notifications.length})
          </button>
          <button
            onClick={() => setFilterStatus("nao-lidas")}
            className={`px-3 py-1.5 text-sm transition-all flex items-center gap-1.5 ${
              filterStatus === "nao-lidas"
                ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
                : "bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--accent)]"
            }`}
            style={{ borderRadius: "var(--radius)" }}
          >
            <Mail24Regular className="w-4 h-4" />
            Não lidas ({unreadCount})
          </button>
          <button
            onClick={() => setFilterStatus("lidas")}
            className={`px-3 py-1.5 text-sm transition-all flex items-center gap-1.5 ${
              filterStatus === "lidas"
                ? "bg-[var(--primary)] text-[var(--primary-foreground)]"
                : "bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--accent)]"
            }`}
            style={{ borderRadius: "var(--radius)" }}
          >
            <MailRead24Regular className="w-4 h-4" />
            Lidas ({notifications.length - unreadCount})
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Mail24Regular className="w-16 h-16 text-[var(--muted-foreground)] mb-4" />
            <h3 className="text-card-foreground mb-2">
              Nenhuma notificação
            </h3>
            <p className="text-sm text-[var(--muted-foreground)] text-center max-w-md">
              {filterStatus === "nao-lidas"
                ? "Não tem notificações não lidas."
                : filterStatus === "lidas"
                  ? "Não tem notificações lidas."
                  : "Não tem notificações."}
            </p>
          </div>
        ) : (
          <div>
            {filteredNotifications.map((notification, index) => {
              const isLast =
                index === filteredNotifications.length - 1;
              return (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-[var(--accent)] transition-colors ${
                    !notification.isRead ? "bg-accent/50" : ""
                  } ${!isLast ? "border-b border-[var(--border)]" : ""}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 flex items-center justify-center flex-shrink-0 ${getIconColor(
                        notification.icon,
                      )}`}
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      {getNotificationIcon(notification.icon)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-card-foreground">
                              {notification.title}
                            </h3>
                            {!notification.isRead && (
                              <div
                                className="w-2 h-2 bg-[var(--primary)] flex-shrink-0"
                                style={{
                                  borderRadius: "var(--radius-full)",
                                }}
                              />
                            )}
                          </div>
                          <p className="text-sm text-[var(--muted-foreground)] mb-2">
                            {notification.description}
                          </p>
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-[var(--muted-foreground)]">
                              {notification.timestamp}
                            </span>
                            <span
                              className="text-xs px-2 py-0.5 bg-[var(--secondary)] text-[var(--secondary-foreground)]"
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              {notification.categoria}
                            </span>
                          </div>
                        </div>

                        <div className="relative">
                          <button
                            onClick={() =>
                              setActiveMenuId(
                                activeMenuId === notification.id
                                  ? null
                                  : notification.id,
                              )
                            }
                            className="p-2 hover:bg-[var(--accent)] transition-colors"
                            style={{ borderRadius: "var(--radius)" }}
                            aria-label="Mais opções"
                          >
                            <MoreVertical20Regular className="text-[var(--muted-foreground)]" />
                          </button>

                          {activeMenuId === notification.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setActiveMenuId(null)}
                              />

                              <div
                                className="absolute right-0 top-full mt-1 w-56 bg-card border border-[var(--border)] shadow-lg z-20"
                                style={{ borderRadius: "var(--radius)" }}
                              >
                                <div className="py-1">
                                  {notification.isRead ? (
                                    <button
                                      onClick={() =>
                                        handleMarkAsUnread(notification.id)
                                      }
                                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-[var(--accent)] transition-colors flex items-center gap-3"
                                    >
                                      <Mail24Regular className="w-4 h-4" />
                                      Marcar como não lida
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() =>
                                        handleMarkAsRead(notification.id)
                                      }
                                      className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-[var(--accent)] transition-colors flex items-center gap-3"
                                    >
                                      <MailRead24Regular className="w-4 h-4" />
                                      Marcar como lida
                                    </button>
                                  )}

                                  <div className="h-px bg-border my-1" />

                                  <button
                                    onClick={() =>
                                      handleDelete(notification.id)
                                    }
                                    className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-3"
                                  >
                                    <Delete20Regular className="w-4 h-4" />
                                    Eliminar
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
